import { ReactNode } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ComboSelect, TermChip } from '../../atoms';

// Type/source-type options that also carry the term (duration) used to render a chip.
export type TermOptionItem = { label: string; value: string; duration?: string };

export interface TermTypeSelectProps {
    options: TermOptionItem[];
    value: string;
    // eslint-disable-next-line no-unused-vars
    onChange: (value: string, term?: string) => void;
    error?: boolean;
    showTooltipError?: boolean;
    openTooltipError?: boolean;
    errorText?: ReactNode;
}

// Dropdown option content: the term chip rendered as a prefix to the option label.
const TermOptionContent = ({ label, term }: { label: string; term?: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        <TermChip term={term} variant="bordered" />
        <Box component="span">{label}</Box>
    </Box>
);

// ComboSelect wrapper that renders a term chip as a prefix on each option and on the selected
// value, and reports both the chosen value and its term back to the caller.
export const TermTypeSelect = ({
    options,
    value,
    onChange,
    error,
    showTooltipError,
    openTooltipError,
    errorText
}: TermTypeSelectProps) => {
    const { t } = useTranslation();
    return (
        <ComboSelect
            options={options}
            selectedValue={value ?? ''}
            onSelectChange={(val) => {
                const selected = Array.isArray(val) ? val[0] ?? '' : val;
                const option = options.find((o) => o.value === selected);
                onChange(selected, option?.duration);
            }}
            size="small"
            fullWidth
            error={error}
            showTooltipError={showTooltipError}
            openTooltipError={openTooltipError}
            errorText={errorText}
            renderMenuItem={(option: TermOptionItem) => (
                <TermOptionContent label={option.label} term={option.duration} />
            )}
            renderValue={(selected) => {
                const option = options.find((o) => o.value === selected);
                if (!option) return <em style={{ fontStyle: 'normal' }}>{t('TEXT_SELECT')}</em>;
                return <TermOptionContent label={option.label} term={option.duration} />;
            }}
        />
    );
};
